import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Event {
  id: string;
  restaurant_id: string;
  title: string;
  description: string | null;
  event_date: string;
  event_time: string | null;
  end_time: string | null;
  image_url: string | null;
  is_featured: boolean;
  is_active: boolean;
  location: string | null;
  price: number | null;
  max_participants: number | null;
  recurrence_type: 'none' | 'weekly' | 'biweekly' | 'monthly';
  recurrence_end_date: string | null;
  recurrence_days: number[] | null;
  created_at: string;
  updated_at: string;
}

export interface EventInput {
  title: string;
  description?: string;
  event_date: string;
  event_time?: string;
  end_time?: string;
  image_url?: string;
  is_featured?: boolean;
  is_active?: boolean;
  location?: string;
  price?: number;
  max_participants?: number;
  recurrence_type?: 'none' | 'weekly' | 'biweekly' | 'monthly';
  recurrence_end_date?: string;
  recurrence_days?: number[];
}

export function useEvents(restaurantId: string | null) {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = async () => {
    if (!restaurantId) {
      setEvents([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('event_date', { ascending: true });

      if (error) throw error;
      setEvents((data as Event[]) || []);
    } catch (err: any) {
      setError(err.message);
      toast.error('Erreur lors du chargement des événements');
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async (eventData: EventInput) => {
    if (!restaurantId) return null;

    try {
      const { data, error } = await supabase
        .from('events')
        .insert({
          ...eventData,
          restaurant_id: restaurantId
        })
        .select()
        .single();

      if (error) throw error;
      
      setEvents(prev => [...prev, data as Event]);
      toast.success('Événement créé avec succès');
      return data;
    } catch (err: any) {
      toast.error('Erreur lors de la création: ' + err.message);
      return null;
    }
  };

  const updateEvent = async (id: string, eventData: Partial<EventInput>) => {
    try {
      const { data, error } = await supabase
        .from('events')
        .update(eventData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setEvents(prev => prev.map(e => e.id === id ? (data as Event) : e));
      toast.success('Événement mis à jour');
      return data;
    } catch (err: any) {
      toast.error('Erreur lors de la mise à jour: ' + err.message);
      return null;
    }
  };

  const deleteEvent = async (id: string) => {
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setEvents(prev => prev.filter(e => e.id !== id));
      toast.success('Événement supprimé');
      return true;
    } catch (err: any) {
      toast.error('Erreur lors de la suppression: ' + err.message);
      return false;
    }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    return updateEvent(id, { is_active: isActive });
  };

  const toggleFeatured = async (id: string, isFeatured: boolean) => {
    return updateEvent(id, { is_featured: isFeatured });
  };

  useEffect(() => {
    fetchEvents();
  }, [restaurantId]);

  return {
    events,
    loading,
    error,
    createEvent,
    updateEvent,
    deleteEvent,
    toggleActive,
    toggleFeatured,
    refetch: fetchEvents
  };
}

export function usePublicEvents(restaurantId: string | null) {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPublicEvents = async () => {
      if (!restaurantId) {
        setEvents([]);
        setLoading(false);
        return;
      }

      try {
        // Get today's date in local timezone as YYYY-MM-DD
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        
        // Fetch active events (including recurring ones)
        const { data, error } = await supabase
          .from('events')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .eq('is_active', true)
          .or(`event_date.gte.${today},recurrence_type.neq.none`)
          .order('event_date', { ascending: true })
          .limit(10);

        if (error) throw error;
        
        // Filter and expand recurring events
        const expandedEvents: Event[] = [];
        
        // Parse today as [year, month, day] to avoid timezone issues
        const [todayYear, todayMonth, todayDay] = today.split('-').map(Number);
        const todayTimestamp = new Date(todayYear, todayMonth - 1, todayDay).getTime();
        
        data?.forEach(eventRow => {
          const event = eventRow as Event;
          
          // Parse event date as [year, month, day]
          const [eventYear, eventMonth, eventDay] = event.event_date.split('-').map(Number);
          let eventTimestamp = new Date(eventYear, eventMonth - 1, eventDay).getTime();
          
          if (event.recurrence_type === 'none') {
            // Non-recurring event - only show if not past
            if (eventTimestamp >= todayTimestamp) {
              expandedEvents.push(event);
            }
          } else {
            // Recurring event - calculate next occurrence(s)
            let endTimestamp: number | null = null;
            if (event.recurrence_end_date) {
              const [endYear, endMonth, endDay] = event.recurrence_end_date.split('-').map(Number);
              endTimestamp = new Date(endYear, endMonth - 1, endDay).getTime();
            }
            
            // If end date is past, skip
            if (endTimestamp !== null && endTimestamp < todayTimestamp) return;
            
            // Check if we have multiple days specified
            const recurrenceDays = event.recurrence_days && event.recurrence_days.length > 0 
              ? event.recurrence_days 
              : null;
            
            if (recurrenceDays && (event.recurrence_type === 'weekly' || event.recurrence_type === 'biweekly')) {
              // Multi-day recurring event
              recurrenceDays.forEach(targetDay => {
                // Find next occurrence for this day
                const todayDate = new Date(todayYear, todayMonth - 1, todayDay);
                const currentDayOfWeek = todayDate.getDay();
                
                let daysUntilTarget = targetDay - currentDayOfWeek;
                // If the day has passed this week, go to next week
                if (daysUntilTarget < 0) daysUntilTarget += 7;
                // If it's today, daysUntilTarget is 0, which is correct
                
                const nextDate = new Date(todayDate);
                nextDate.setDate(todayDate.getDate() + daysUntilTarget);
                
                // Check if within end date
                if (endTimestamp === null || nextDate.getTime() <= endTimestamp) {
                  const year = nextDate.getFullYear();
                  const month = String(nextDate.getMonth() + 1).padStart(2, '0');
                  const day = String(nextDate.getDate()).padStart(2, '0');
                  
                  expandedEvents.push({
                    ...event,
                    event_date: `${year}-${month}-${day}`
                  });
                }
              });
            } else {
              // Single day recurring - use original logic
              let nextDate = new Date(eventYear, eventMonth - 1, eventDay);
              
              while (nextDate.getTime() < todayTimestamp) {
                switch (event.recurrence_type) {
                  case 'weekly':
                    nextDate.setDate(nextDate.getDate() + 7);
                    break;
                  case 'biweekly':
                    nextDate.setDate(nextDate.getDate() + 14);
                    break;
                  case 'monthly':
                    nextDate.setMonth(nextDate.getMonth() + 1);
                    break;
                }
              }
              
              // Check if next occurrence is within end date
              if (endTimestamp === null || nextDate.getTime() <= endTimestamp) {
                const year = nextDate.getFullYear();
                const month = String(nextDate.getMonth() + 1).padStart(2, '0');
                const day = String(nextDate.getDate()).padStart(2, '0');
                
                expandedEvents.push({
                  ...event,
                  event_date: `${year}-${month}-${day}`
                });
              }
            }
          }
        });
        
        // Sort by date and limit
        expandedEvents.sort((a, b) => a.event_date.localeCompare(b.event_date));
        setEvents(expandedEvents.slice(0, 5));
      } catch (err) {
        console.error('Error fetching public events:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicEvents();
  }, [restaurantId]);

  return { events, loading };
}
